import { refreshSession } from "./auth-api";
import { isDefinitiveRejection, mayRetainAfterTransportFailure } from "./refresh-error";
import { getConfig } from "../client/config";
import { refreshedTokenMayReplaceStored } from "./session-binding";
import { notifySessionCleared } from "./session-events";
import { decideCommit, mintSessionGeneration, withSessionLock, } from "./session-mutation";
let _config = null;
export class ReactNativeSessionManager {
    /**
     * Must be called once before any other method.
     *
     * SECURITY: the persisted blob includes the long-lived refresh token, so the
     * `storage` adapter MUST encrypt at rest. The supported pattern is MMKV with an
     * `encryptionKey` held in the OS keystore (Keychain on iOS / Keystore on Android)
     * via react-native-keychain - NOT a keyless MMKV instance, and NEVER a hardcoded key.
     *
     * ```ts
     * import { ReactNativeSessionManager } from '@bounded-sh/core';
     * import { MMKV } from 'react-native-mmkv';
     * import * as Keychain from 'react-native-keychain';
     * import { decode } from 'base-64';
     *
     * // Fetch (or lazily mint) a random 256-bit key stored in the OS keystore.
     * // The keystore - not the app bundle - is the root of trust for the key.
     * async function getMmkvEncryptionKey(): Promise<string> {
     *   const service = 'sh.bounded.session.mmkv';
     *   const existing = await Keychain.getGenericPassword({ service });
     *   if (existing) return existing.password;
     *   const bytes = crypto.getRandomValues(new Uint8Array(32));
     *   const key = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
     *   await Keychain.setGenericPassword('bounded', key, { service });
     *   return key;
     * }
     *
     * const encryptionKey = await getMmkvEncryptionKey();
     * const mmkv = new MMKV({ id: 'bounded-session', encryptionKey });
     * ReactNativeSessionManager.configure({
     *   storage: {
     *     getItem: (k) => mmkv.getString(k) ?? null,
     *     setItem: (k, v) => mmkv.set(k, v),
     *     removeItem: (k) => mmkv.delete(k),
     *   },
     *   atob: decode,
     * });
     * ```
     *
     * Unencrypted adapters (bare MMKV, AsyncStorage) are a credential-theft risk and
     * require an explicit opt-in - the manager cannot verify encryption for you:
     *
     * ```ts
     * // Development only: acknowledge that tokens are stored in plaintext.
     * ReactNativeSessionManager.configure({ storage, atob: decode, allowUnsafePlaintext: true });
     * ```
     */
    static configure(cfg) {
        if (cfg.allowUnsafePlaintext === true) {
            // The developer explicitly opted into a plaintext store. Make it loud so it is
            // never a silent production default. (We cannot detect plaintext for the
            // non-opted-in case, hence the required, documented acknowledgment flag.)
            console.warn("[ReactNativeSessionManager] configure(): allowUnsafePlaintext=true - the session " +
                "(including the long-lived refresh token) is being stored UNENCRYPTED. Use an " +
                "encrypted backend (MMKV with a Keychain-held encryptionKey / expo-secure-store) " +
                "before shipping; plaintext tokens are readable from a rooted device or a backup.");
        }
        _config = cfg;
    }
    /**
     * Whether configure() has been called. This is the canonical "are we on
     * React Native?" signal used by getActiveSessionManager(): RN consumers MUST
     * call configure() at startup, while web/Node never do. Far more reliable
     * than sniffing `typeof window` (React Native defines a global `window`, so
     * that test misclassifies RN as web and routes session writes to a
     * non-existent localStorage).
     */
    static isConfigured() {
        return _config !== null;
    }
    static getStorage() {
        if (!_config) {
            throw new Error("ReactNativeSessionManager.configure() must be called before using session methods.");
        }
        return _config.storage;
    }
    static decodeBase64(input) {
        if (!_config) {
            throw new Error("ReactNativeSessionManager.configure() must be called before using session methods.");
        }
        return _config.atob(input);
    }
    /**
     * Decode a base64url-encoded string (used by JWT payloads).
     * Normalises base64url → standard base64 before decoding.
     */
    static decodeBase64Url(input) {
        let b64 = input.replace(/-/g, '+').replace(/_/g, '/');
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        return this.decodeBase64(b64);
    }
    /* ------------------------------------------------------------------ */
    /* STORE                                                              */
    /* ------------------------------------------------------------------ */
    static async storeSession(address, accessToken, idToken, refreshToken, issuer) {
        var _a;
        // JWT-wallet binding: refuse to store a session whose idToken is bound
        // to a different wallet than `address`. Prevents races that would otherwise
        // leave storage with mismatched address/token state.
        try {
            const payloadB64 = idToken.split(".")[1];
            if (payloadB64) {
                const payload = JSON.parse(this.decodeBase64Url(payloadB64));
                const tokenWallet = payload["custom:walletAddress"];
                if (tokenWallet && tokenWallet !== address) {
                    throw new Error(`[ReactNativeSessionManager] Refusing to store session: address (${address}) does not match idToken custom:walletAddress (${tokenWallet})`);
                }
                if (!tokenWallet) {
                    console.warn("[ReactNativeSessionManager] storeSession: idToken has no custom:walletAddress claim — writing without validation");
                }
            }
        }
        catch (err) {
            if (typeof (err === null || err === void 0 ? void 0 : err.message) === "string" && err.message.includes("Refusing to store session")) {
                throw err;
            }
            console.warn("[ReactNativeSessionManager] storeSession: failed to decode idToken for validation. reason:", (_a = err === null || err === void 0 ? void 0 : err.name) !== null && _a !== void 0 ? _a : "decode_failed");
        }
        const config = await getConfig();
        const currentAppId = config.appId;
        // Issuer base for silent refresh (email overrides w/ humanAuthApiUrl; wallet/
        // guest default to authApiUrl). See WebSessionManager for the rationale.
        const sessionIssuer = (issuer || config.authApiUrl || "").replace(/\/$/, "");
        // A login always starts a NEW generation; refresh commits preserve it.
        // See WebSessionManager for why lineage cannot be inferred from tokens or
        // address. RN is single-realm, so the lock resolves to direct execution.
        await withSessionLock(() => {
            this.writeRaw(Object.assign({ address,
                accessToken,
                idToken,
                refreshToken, appId: currentAppId, sessionGeneration: mintSessionGeneration() }, (sessionIssuer ? { issuer: sessionIssuer } : {})));
        });
    }
    /** Raw read of the stored blob. Callers inside the lock use this, never a cached copy. */
    static readRaw() {
        try {
            const raw = this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        }
        catch (_a) {
            return null;
        }
    }
    static writeRaw(sessionObj) {
        this.getStorage().setItem(this.BOUNDED_SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
    }
    /** Mirror of WebSessionManager.readSessionWithGeneration. */
    static async readSessionWithGeneration() {
        const existing = this.readRaw();
        if (!existing)
            return null;
        if (existing.sessionGeneration)
            return existing;
        return await withSessionLock(() => {
            const current = this.readRaw();
            if (!current)
                return null;
            if (current.sessionGeneration)
                return current;
            const upgraded = Object.assign(Object.assign({}, current), { sessionGeneration: mintSessionGeneration() });
            this.writeRaw(upgraded);
            return upgraded;
        });
    }
    /* ------------------------------------------------------------------ */
    /* GET                                                                */
    /* ------------------------------------------------------------------ */
    static async getSession() {
        var _a;
        const sessionObj = await this.readSessionWithGeneration();
        if (!sessionObj)
            return null;
        const generation = (_a = sessionObj.sessionGeneration) !== null && _a !== void 0 ? _a : null;
        /* ---------- validate app-id ---------- */
        const config = await getConfig();
        if (sessionObj.appId && sessionObj.appId !== config.appId) {
            await this.clearSessionIfGeneration(generation);
            return null;
        }
        /* ---------- check JWT expiration ---------- */
        // No skew buffer, deliberately: this is the restore path, and at boot a token
        // already past `exp` cannot authenticate anything. See WebSessionManager.
        try {
            // Expiry on the ID TOKEN (always a JWT). The OIDC access_token for email/social
            // logins is opaque, so decoding it threw → the catch wiped a valid session.
            const { idToken } = sessionObj;
            const { exp } = JSON.parse(this.decodeBase64Url(idToken.split(".")[1]));
            // Treat a MISSING or non-numeric expiry as EXPIRED (fail closed). A bare
            // `Date.now() > exp * 1000` is NaN -> false when exp is absent/non-numeric,
            // so a forged or malformed token with no usable expiry would otherwise be
            // accepted as valid forever.
            const expired = typeof exp !== "number" || !Number.isFinite(exp) || Date.now() > exp * 1000;
            if (expired) {
                const { refreshToken } = sessionObj;
                // Expired token with no way to refresh — clear the stale session
                // (mirrors the refresh-failed branch below) so isAuthenticated()
                // does not stay true and every request does not 401 + retry forever.
                if (!refreshToken) {
                    await this.clearSessionIfGeneration(generation);
                    return null;
                }
                let refreshed;
                try {
                    refreshed = await refreshSession(refreshToken, sessionObj.issuer);
                }
                catch (refreshErr) {
                    // DS4-0054: only a DEFINITIVE server rejection (401/403) proves the
                    // refresh token is dead. A transport failure - offline, DNS, timeout,
                    // 5xx - is unknown, not proof the session is invalid. On mobile these
                    // blips are common, so destroying local state here signs the user out
                    // on a bad network. RETAIN it and return the existing session; the next
                    // request retries the refresh. The retention is BOUNDED by the token's
                    // own expiry plus a grace window, so a revoked session - or an issuer
                    // 401 a gateway rewrote into a 5xx - cannot keep isAuthenticated() true
                    // forever on a device that never reaches the issuer again.
                    if (isDefinitiveRejection(refreshErr) || !mayRetainAfterTransportFailure(exp)) {
                        await this.clearSessionIfGeneration(generation);
                        return null;
                    }
                    return { address: sessionObj.address, session: sessionObj };
                }
                if ((refreshed === null || refreshed === void 0 ? void 0 : refreshed.idToken) && (refreshed === null || refreshed === void 0 ? void 0 : refreshed.accessToken)) {
                    const committed = await this.commitRefreshedTokens({ generation, idToken: sessionObj.idToken }, refreshed.idToken, refreshed.accessToken, refreshed.refreshToken);
                    // Superseded means a login or logout landed mid-refresh. Returning
                    // our stale null would sign the UI out of a session that is now
                    // valid, since init() applies whatever restoreSession() returns.
                    if (committed.outcome === "superseded") {
                        const current = this.readRaw();
                        if (!current)
                            return null;
                        return { address: current.address, session: current };
                    }
                    const newObj = committed.session;
                    if (!newObj)
                        return null;
                    return { address: newObj.address, session: newObj };
                }
                // The server answered but issued no usable tokens - a definitive refusal,
                // not a transport failure - so clear the stale session to stop retry loops,
                // but only if it is still the session we set out to refresh.
                await this.clearSessionIfGeneration(generation);
                return null;
            }
        }
        catch (err) {
            // Token decode or refresh failed — clear stale session to prevent retry loops
            await this.clearSessionIfGeneration(generation);
            return null;
        }
        return { address: sessionObj.address, session: sessionObj };
    }
    /* ------------------------------------------------------------------ */
    /* CLEAR                                                              */
    /* ------------------------------------------------------------------ */
    /**
     * Remove the session unconditionally. Async and awaited for the same reason as
     * the web manager: a caller that does not await lets logout() return while the
     * credential is still readable.
     */
    static async clearSession() {
        const removed = await withSessionLock(() => {
            if (this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY) === null)
                return false;
            this.getStorage().removeItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return true;
        });
        if (removed)
            notifySessionCleared();
    }
    /** Remove only if the stored session is still the one identified by `generation`. */
    static async clearSessionIfGeneration(generation) {
        const removed = await withSessionLock(() => {
            var _a;
            const current = this.readRaw();
            if (!current)
                return false;
            if (generation === null)
                return false;
            if (((_a = current.sessionGeneration) !== null && _a !== void 0 ? _a : null) !== generation)
                return false;
            this.getStorage().removeItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return true;
        });
        if (removed)
            notifySessionCleared();
    }
    /* ------------------------------------------------------------------ */
    /* IS-AUTH                                                            */
    /* ------------------------------------------------------------------ */
    static isAuthenticated() {
        return !!this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY);
    }
    /* ------------------------------------------------------------------ */
    /* TOKEN HELPERS                                                      */
    /* ------------------------------------------------------------------ */
    static getIdToken() {
        try {
            const session = this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).idToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    /** The issuer base that minted this session (for routing silent refresh). */
    static getIssuer() {
        var _a;
        try {
            const session = this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? ((_a = JSON.parse(session).issuer) !== null && _a !== void 0 ? _a : null) : null;
        }
        catch (_b) {
            return null;
        }
    }
    static getRefreshToken() {
        try {
            const session = this.getStorage().getItem(this.BOUNDED_SESSION_STORAGE_KEY);
            return session ? JSON.parse(session).refreshToken : null;
        }
        catch (_a) {
            return null;
        }
    }
    /* ------------------------------------------------------------------ */
    /* UPDATE TOKENS                                                      */
    /* ------------------------------------------------------------------ */
    /**
     * Commit refreshed tokens bound to the generation they were minted for.
     * Mirror of WebSessionManager.commitRefreshedTokens; see it for the rationale.
     */
    static async commitRefreshedTokens(expected, idToken, accessToken, refreshToken) {
        // Resolve config BEFORE the lock: no awaits inside the critical section.
        const config = await getConfig();
        const result = await withSessionLock(() => {
            var _a;
            const current = this.readRaw();
            /* ---------- app-id guard ---------- */
            if ((current === null || current === void 0 ? void 0 : current.appId) && current.appId !== config.appId) {
                this.getStorage().removeItem(this.BOUNDED_SESSION_STORAGE_KEY);
                return { outcome: "superseded", session: null, cleared: true };
            }
            const next = Object.assign(Object.assign(Object.assign({}, (current !== null && current !== void 0 ? current : {})), { idToken,
                accessToken }), (typeof refreshToken === "string" && refreshToken.length > 0
                ? { refreshToken }
                : {}));
            const outcome = decideCommit(current, expected, next);
            if (outcome === "superseded")
                return { outcome, session: null };
            if (outcome === "same-generation-winner")
                return { outcome, session: current };
            if (outcome === "already-equal")
                return { outcome, session: current };
            /* ---------- cross-principal guard (#220) ---------- */
            if (!refreshedTokenMayReplaceStored(idToken, current.address, (s) => this.decodeBase64Url(s))) {
                console.warn("[ReactNativeSessionManager] commitRefreshedTokens: refreshed token wallet does not match the stored session address — discarding stale refresh");
                return { outcome: "superseded", session: null };
            }
            (_a = next.appId) !== null && _a !== void 0 ? _a : (next.appId = config.appId);
            this.writeRaw(next);
            return { outcome, session: next };
        });
        if (result.cleared)
            notifySessionCleared();
        return { outcome: result.outcome, session: result.session };
    }
}
ReactNativeSessionManager.BOUNDED_SESSION_STORAGE_KEY = "bounded_session_storage";
